<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">時段分析</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleExport">
          <i class="ri-download-line"></i>
          匯出 Excel
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <label>開始日期</label>
        <input
          v-model="filters.startDate"
          type="date"
          class="form-control"
          required
        />
      </div>
      <div class="filter-group">
        <label>結束日期</label>
        <input
          v-model="filters.endDate"
          type="date"
          class="form-control"
          required
        />
      </div>
      <button class="btn btn-primary" @click="fetchData">查詢</button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchData">重試</button>
    </div>

    <div v-else class="dashboard-content">
      <!-- 每小時分布圖 -->
      <div class="chart-section">
        <div class="chart-card">
          <BarChart
            v-if="hourlyData.length > 0"
            title="每小時業績分布"
            :data="hourlyData"
            xAxisKey="hour"
            yAxisKey="totalRevenue"
            height="400px"
            color="#4facfe"
          />
        </div>
      </div>

      <!-- 平日/假日對比 -->
      <div class="chart-section">
        <div class="chart-card">
          <BarChart
            v-if="weekdayData.length > 0"
            title="平日/假日業績對比"
            :data="weekdayData"
            xAxisKey="dayType"
            yAxisKey="totalRevenue"
            height="300px"
            color="#667eea"
          />
        </div>
      </div>

      <!-- 尖峰時段提示 -->
      <div v-if="peakHours.length > 0" class="info-card">
        <h3>尖峰時段</h3>
        <div class="peak-hours">
          <span
            v-for="hour in peakHours"
            :key="hour"
            class="badge badge-primary"
          >
            {{ hour }}:00
          </span>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { reportsService } from '@/api/modules/reports.js';
import { showError, showSuccess } from '@/utils/swal.js';
import BarChart from '@/components/charts/BarChart.vue';

export default {
  name: 'TimeAnalysis',
  components: {
    BarChart
  },
  setup() {
    const loading = ref(false);
    const error = ref(null);
    const analysisData = ref({});
    const filters = ref({
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0]
    });

    const hourlyData = computed(() => {
      if (!analysisData.value.hourly) return [];
      return analysisData.value.hourly.map(item => ({
        hour: `${item.hour}:00`,
        totalRevenue: item.totalRevenue,
        orderCount: item.orderCount,
        customerCount: item.customerCount
      }));
    });

    const weekdayData = computed(() => {
      if (!analysisData.value.weekday) return [];
      return analysisData.value.weekday.map(item => ({
        dayType: item.dayType === 'weekend' ? '假日' : '平日',
        totalRevenue: item.totalRevenue,
        orderCount: item.orderCount
      }));
    });

    const peakHours = computed(() => {
      return analysisData.value.peakHours || [];
    });

    const fetchData = async () => {
      if (!filters.value.startDate || !filters.value.endDate) {
        showError('錯誤', '請選擇開始日期和結束日期');
        return;
      }

      try {
        loading.value = true;
        error.value = null;
        const response = await reportsService.getTimeAnalysis(filters.value);
        if (response.success) {
          analysisData.value = response.data;
        } else {
          error.value = response.message || '取得資料失敗';
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '取得資料失敗';
        showError('錯誤', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleExport = async () => {
      if (!filters.value.startDate || !filters.value.endDate) {
        showError('錯誤', '請先選擇日期範圍');
        return;
      }

      try {
        loading.value = true;
        const params = {
          reportType: 'time-analysis',
          format: 'excel',
          ...filters.value
        };
        const blob = await reportsService.exportReport(params);
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `時段分析_${filters.value.startDate}_${filters.value.endDate}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showSuccess('成功', '報表已匯出');
      } catch (err) {
        showError('匯出失敗', err.response?.data?.message || err.message || '匯出失敗');
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchData();
    });

    return {
      loading,
      error,
      analysisData,
      filters,
      hourlyData,
      weekdayData,
      peakHours,
      fetchData,
      handleExport
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.chart-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.chart-card {
  width: 100%;
}

.info-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.info-card h3 {
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
}

.peak-hours {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
</style>
